import React from 'react';
import SidebarAccordion, { SIDEBAR_SECTIONS } from './SidebarTabs';
import ElementsPanel from './ElementsPanel';
import PalettePanel from './PalettePanel';
import ModuleSettingsPanel from './ModuleSettingsPanel';
import LayoutPanel from './LayoutPanel';
import AIDesignerPanel from './AIDesignerPanel';

/**
 * Sidebar component for GuiBuilder
 * Accordion sections for modules, elements, palette, structure, and style assistant
 */
export default function Sidebar({
    openSidebarSections,
    onToggleSidebarSection,
    pages,
    properties,
    setProperties,
    targetUrl,
    setTargetUrl,
    aiPrompt,
    setAiPrompt,
    isGenerating,
    handleStartDesignJob,
    generationStatus,
    familyId,
    familyName,
    linkedFamilies,
    addHeader,
    addDescription,
    addExitButton,
    moveBlock,
    features = []
}) {
    const isOpen = (id) => openSidebarSections.includes(id);

    const renderPanel = (id) => {
        switch (id) {
            case 'modules':
                return (
                    <ModuleSettingsPanel
                        properties={properties}
                        setProperties={setProperties}
                        features={features}
                    />
                );
            case 'elements':
                return (
                    <ElementsPanel
                        pages={pages}
                        addHeader={addHeader}
                        addDescription={addDescription}
                        addExitButton={addExitButton}
                    />
                );
            case 'palette':
                return (
                    <PalettePanel
                        familyId={familyId}
                        familyName={familyName}
                        linkedFamilies={linkedFamilies}
                        placedFeatureNames={pages.flatMap(p => p.elements)
                            .filter(el => el.type === 'variable' || el.type === 'feature')
                            .map(el => el.featureName || el.variableName)}
                    />
                );
            case 'layout':
                return (
                    <LayoutPanel
                        properties={properties}
                        setProperties={setProperties}
                        moveBlock={moveBlock}
                    />
                );
            case 'ai-design':
                return (
                    <AIDesignerPanel
                        targetUrl={targetUrl}
                        setTargetUrl={setTargetUrl}
                        aiPrompt={aiPrompt}
                        setAiPrompt={setAiPrompt}
                        isGenerating={isGenerating}
                        handleStartDesignJob={handleStartDesignJob}
                        generationStatus={generationStatus}
                        properties={properties}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div style={{
            width: '340px',
            minWidth: '340px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.65rem',
            maxHeight: '80vh',
            overflowY: 'auto'
        }}>
            {SIDEBAR_SECTIONS.map(section => (
                <SidebarAccordion
                    key={section.id}
                    id={section.id}
                    label={section.label}
                    open={isOpen(section.id)}
                    onToggle={onToggleSidebarSection}
                >
                    {isOpen(section.id) ? renderPanel(section.id) : null}
                </SidebarAccordion>
            ))}
        </div>
    );
}

Sidebar.displayName = 'GuiBuilder.Sidebar';
